import api from './client'

export interface CustomStatus {
  id: string
  applyTo: 'lead' | 'deal'
  label: string
  color: string
  sortOrder: number
  isDefault: boolean
}

export interface CustomField {
  id: string
  applyTo: 'lead' | 'deal'
  fieldKey: string
  label: string
  fieldType: string
  options: { label: string; value: string }[] | null
  isRequired: boolean
  isActive: boolean
  isFromSource: boolean
  sortOrder: number
}

export interface SavedView {
  id: string
  name: string
  refType: 'lead' | 'deal'
  filters: Record<string, unknown>[]
  columns: string[]
  sortField: string | null
  sortOrder: 'ASC' | 'DESC' | null
  viewType: 'list' | 'kanban'
  isPublic: boolean
  isPinned: boolean
  createdById: string
  createdAt: string
}

export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  isActive: boolean
  companyId: string
  organizationIds: string[]
  canSeePhone?: boolean
  canSeeEmail?: boolean
  canSeeWhatsapp?: boolean
  canSeeRecordings?: boolean
  canEditServicePrices?: boolean
  createdAt: string
}

export const customStatusesApi = {
  list: (companyId: string) =>
    api.get<CustomStatus[]>(`/companies/${companyId}/custom-statuses`),
  create: (companyId: string, dto: Partial<CustomStatus>) =>
    api.post<CustomStatus>(`/companies/${companyId}/custom-statuses`, dto),
  update: (companyId: string, id: string, dto: Partial<CustomStatus>) =>
    api.patch<CustomStatus>(`/companies/${companyId}/custom-statuses/${id}`, dto),
  remove: (companyId: string, id: string) =>
    api.delete(`/companies/${companyId}/custom-statuses/${id}`),
  reorder: (companyId: string, ids: string[]) =>
    api.patch(`/companies/${companyId}/custom-statuses/reorder`, { ids }),
}

export const customFieldsApi = {
  list: (companyId: string, applyTo?: 'lead' | 'deal') =>
    api.get<CustomField[]>(`/companies/${companyId}/custom-fields${applyTo ? `?applyTo=${applyTo}` : ''}`),
  create: (companyId: string, dto: Partial<CustomField>) =>
    api.post<CustomField>(`/companies/${companyId}/custom-fields`, dto),
  update: (companyId: string, id: string, dto: Partial<CustomField>) =>
    api.patch<CustomField>(`/companies/${companyId}/custom-fields/${id}`, dto),
  remove: (companyId: string, id: string) =>
    api.delete(`/companies/${companyId}/custom-fields/${id}`),
}

export const savedViewsApi = {
  list: (companyId: string, refType?: string) =>
    api.get<SavedView[]>(`/companies/${companyId}/views`, { params: refType ? { refType } : {} }),
  create: (companyId: string, dto: Partial<SavedView>) =>
    api.post<SavedView>(`/companies/${companyId}/views`, dto),
  update: (companyId: string, id: string, dto: Partial<SavedView>) =>
    api.patch<SavedView>(`/companies/${companyId}/views/${id}`, dto),
  pin: (companyId: string, id: string) =>
    api.patch(`/companies/${companyId}/views/${id}/pin`),
  remove: (companyId: string, id: string) =>
    api.delete(`/companies/${companyId}/views/${id}`),
}

export const usersApi = {
  list: (companyId: string) =>
    api.get<User[]>(`/companies/${companyId}/users`),
  create: (companyId: string, dto: Partial<User> & { password?: string; organizationIds?: string[] }) =>
    api.post<User>(`/companies/${companyId}/users`, dto),
  update: (companyId: string, id: string, dto: Partial<User>) =>
    api.patch<User>(`/companies/${companyId}/users/${id}`, dto),
  // Backend route is DELETE /:id which sets isActive=false (deactivate, not soft-delete).
  deactivate: (companyId: string, id: string) =>
    api.delete(`/companies/${companyId}/users/${id}`),
}

export const emailAccountsApi = {
  list: (companyId: string) =>
    api.get(`/companies/${companyId}/settings/email-accounts`),
  verify: (companyId: string, dto: Record<string, unknown>) =>
    api.post<{ smtp: boolean; imap: boolean; smtpError?: string; imapError?: string }>(
      `/companies/${companyId}/settings/email-accounts/verify`, dto,
    ),
  create: (companyId: string, dto: Record<string, unknown>) =>
    api.post(`/companies/${companyId}/settings/email-accounts`, dto),
  update: (companyId: string, id: string, dto: Record<string, unknown>) =>
    api.patch(`/companies/${companyId}/settings/email-accounts/${id}`, dto),
  remove: (companyId: string, id: string) =>
    api.delete(`/companies/${companyId}/settings/email-accounts/${id}`),
}

export const emailTemplatesApi = {
  list: (companyId: string) =>
    api.get(`/companies/${companyId}/email-templates`),
  create: (companyId: string, dto: Record<string, unknown>) =>
    api.post(`/companies/${companyId}/email-templates`, dto),
  update: (companyId: string, id: string, dto: Record<string, unknown>) =>
    api.patch(`/companies/${companyId}/email-templates/${id}`, dto),
  remove: (companyId: string, id: string) =>
    api.delete(`/companies/${companyId}/email-templates/${id}`),
}

export const leadSourcesApi = {
  webhookConfig: (companyId: string) =>
    api.get<{ callbackUrl: string; verifyToken: string }>(
      `/companies/${companyId}/lead-sources/webhook-config`,
    ),
  list: (companyId: string) =>
    api.get(`/companies/${companyId}/lead-sources`),
  create: (companyId: string, dto: Record<string, unknown>) =>
    api.post(`/companies/${companyId}/lead-sources`, dto),
  update: (companyId: string, id: string, dto: Record<string, unknown>) =>
    api.patch(`/companies/${companyId}/lead-sources/${id}`, dto),
  remove: (companyId: string, id: string) =>
    api.delete(`/companies/${companyId}/lead-sources/${id}`),
  syncNow: (companyId: string, id: string) =>
    api.post(`/companies/${companyId}/lead-sources/${id}/sync-now`),
  revealSecrets: (companyId: string, id: string) =>
    api.get<{ accessToken: string | null; appSecret: string | null }>(
      `/companies/${companyId}/lead-sources/${id}/reveal-secrets`,
    ),
  failures: (companyId: string, id: string) =>
    api.get(`/companies/${companyId}/lead-sources/${id}/failures`),
  retry: (companyId: string, id: string, failureId: string) =>
    api.post(`/companies/${companyId}/lead-sources/${id}/failures/${failureId}/retry`),
}

export const assignmentRulesApi = {
  list: (companyId: string, includeInactive = false) =>
    api.get(`/companies/${companyId}/assignment-rules`, {
      params: includeInactive ? { includeInactive: 'true' } : undefined,
    }),
  create: (companyId: string, dto: Record<string, unknown>) =>
    api.post(`/companies/${companyId}/assignment-rules`, dto),
  update: (companyId: string, id: string, dto: Record<string, unknown>) =>
    api.patch(`/companies/${companyId}/assignment-rules/${id}`, dto),
  remove: (companyId: string, id: string) =>
    api.delete(`/companies/${companyId}/assignment-rules/${id}`),
}

export const slaPoliciesApi = {
  list: (companyId: string) =>
    api.get(`/companies/${companyId}/sla-policies`),
  create: (companyId: string, dto: Record<string, unknown>) =>
    api.post(`/companies/${companyId}/sla-policies`, dto),
  update: (companyId: string, id: string, dto: Record<string, unknown>) =>
    api.patch(`/companies/${companyId}/sla-policies/${id}`, dto),
  remove: (companyId: string, id: string) =>
    api.delete(`/companies/${companyId}/sla-policies/${id}`),
}

export const adminApi = {
  listCompanies: () => api.get('/admin/companies'),
  createCompany: (dto: Record<string, unknown>) => api.post('/admin/companies', dto),
  createCompanyOwner: (companyId: string, dto: Record<string, unknown>) =>
    api.post(`/admin/companies/${companyId}/owners`, dto),
  getCompany: (id: string) => api.get(`/admin/companies/${id}`),
  updateCompany: (id: string, dto: Record<string, unknown>) => api.patch(`/admin/companies/${id}`, dto),
  deleteCompany: (id: string) => api.delete(`/admin/companies/${id}`),
  reactivateCompany: (id: string) => api.patch(`/admin/companies/${id}/reactivate`),
  checkCompanyName: (name: string) =>
    api.get<{ available: boolean }>('/admin/companies/check-name', { params: { name } }),
  listUsers: () => api.get('/admin/users'),
  // Soft-delete a user. Backend rejects admins, company owners, and self
  // — the UI also gates this to sales users only.
  deleteUser: (id: string) => api.delete(`/admin/users/${id}`),
  // Create a sales user in any company (invite — no password)
  createSalesUser: (companyId: string, dto: Record<string, unknown>) =>
    api.post(`/companies/${companyId}/users`, dto),
}

export interface InvoicingSettings {
  address?: string
  phone?: string
  email?: string
  taxId?: string
  footerNote?: string
  footerNoteIt?: string
  footerNoteEn?: string
  currency?: string
  brandColor?: string
  paymentTerms?: string
}

export const companySettingsApi = {
  getGeneral: (companyId: string) =>
    api.get<{ id: string; name: string; logoUrl?: string; website?: string; timezone?: string; showWhatsappInTimeline?: boolean; recordingRetentionMonths?: number; settings?: { invoicing?: InvoicingSettings } }>(
      `/companies/${companyId}/settings/general`,
    ),
  updateGeneral: (
    companyId: string,
    dto: { name?: string; logoUrl?: string; website?: string; timezone?: string },
  ) => api.patch(`/companies/${companyId}/settings/general`, dto),
  updatePreferences: (companyId: string, dto: { showWhatsappInTimeline?: boolean; recordingRetentionMonths?: number }) =>
    api.patch(`/companies/${companyId}/settings/preferences`, dto),
  updateInvoicing: (companyId: string, dto: InvoicingSettings) =>
    api.patch(`/companies/${companyId}/settings/invoicing`, dto),
}
